import { Button } from '@/components/ui';
import { normalizeHex } from '@/lib/theme/theme-knobs';
import { isEyeDropperSupported, openEyeDropper } from './eyedropper-support';

interface EyeDropperButtonProps {
  /** Called with a normalised `#RRGGBB` once the user picks a colour. */
  onPick: (hex: string) => void;
  disabled?: boolean;
  /** Accessible button label (pass a translated string from the editor). */
  label?: string;
  className?: string;
}

/**
 * Progressive-enhancement button (Spec 340) that opens the native screen colour
 * picker. Renders nothing where the API is unavailable (Firefox/Safari) — never a
 * broken control. The picked colour is normalised before being emitted.
 */
export function EyeDropperButton({
  onPick,
  disabled = false,
  label = 'Pick from screen',
  className,
}: EyeDropperButtonProps) {
  if (!isEyeDropperSupported()) return null;

  const open = async () => {
    const picked = await openEyeDropper();
    if (picked === null) return;
    const normalized = normalizeHex(picked);
    if (normalized) onPick(normalized);
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={open}
      disabled={disabled}
      className={className}
      aria-label={label}
    >
      {label}
    </Button>
  );
}
